import { FC, HTMLAttributes, memo, useEffect, useMemo } from 'react';
import styled from 'styled-components';
import fireBall from '../../assets/apng/fireball.png';
import { useSounds } from '../../hooks/use-sound-handler';
import { Ball30 } from '../Ball30';
import { Ball75 } from '../Ball75';
import { Ball80 } from '../Ball80';
import { Ball90 } from '../Ball90';
import { BallHole } from '../BallHole';
import { BallTray } from '../BallTray';
import { GameTypeNumbers } from '../GameType';

type BallCallsProps = HTMLAttributes<HTMLDivElement> & {
    gameType: GameTypeNumbers;
    calledBalls: number[];
    fireBall?: number;
};

const BallCallsContainer = styled.div`
    display: flex;
    justify-content: flex-end;
    align-items: center;
    position: relative;
    width: 100%;
    min-width: 32.25rem;
    height: 10rem;
`;

const CalledBallsWrapper = styled.div`
    display: flex;
    position: relative;
    width: 100%;
    height: 7.75rem;
`;

const BallTrayItem = styled.div<{ x: number }>`
    user-select: none;
    position: absolute;
    transition: 0.5s ease-in-out left, 0.5s ease-in-out transform;
    transform: ${(props) => `rotate(${props.x * 360}deg)`};
    left: ${(props) => props.x * 6.625 + 'rem'};
`;

const BallHoleItem = styled.div<{ y: number }>`
    display: flex;
    justify-content: center;
    align-items: center;
    user-select: none;
    position: absolute;
    width: 8.938rem;
    height: 8.938rem;
    transition: 0.5s ease-in-out bottom;
    bottom: ${(props) => props.y * 9 + 'rem'};
`;

const BallHoleLayer = styled.div`
    position: absolute;
    top: 0;
    left: 0;
    width: 10rem;
    height: 10rem;
`;

const FireBallAnimation = styled.img`
    position: absolute;
    top: 50%;
    left: 50%;
    width: 20rem;
    height: 20rem;
    transform: translate(-50%, -50%);
    pointer-events: none;
    z-index: 1;
`;

const BALLS = Array.from({ length: 90 }).map((_v, i) => i + 1);
const BALL_COMPONENTS = {
    75: Ball75,
    80: Ball80,
    90: Ball90,
    30: Ball30,
};

export const BallCalls: FC<BallCallsProps> = memo(
    ({ gameType, calledBalls, fireBall: fireBallNumber, ...props }) => {
        const currentIndex = calledBalls.length - 1;
        const previousIndex = currentIndex - 1;
        const currentBall = calledBalls[currentIndex];
        const Ball = BALL_COMPONENTS[gameType];

        const showFireBallAnimation = useMemo(
            () => !!fireBallNumber && currentBall === fireBallNumber,
            [currentBall, fireBallNumber]
        );

        const { play } = useSounds();

        useEffect(() => {
            if (showFireBallAnimation) {
                play('ui', 'burningFire');
            }
        }, [showFireBallAnimation, play]);

        return (
            <BallCallsContainer {...props}>
                <BallTray>
                    <CalledBallsWrapper>
                        {BALLS.map((ball) => {
                            const index = calledBalls.lastIndexOf(
                                ball,
                                previousIndex
                            );

                            const x = index === -1 ? -1 : previousIndex - index;
                            return (
                                <BallTrayItem
                                    as={Ball}
                                    size="small"
                                    key={ball}
                                    number={ball}
                                    x={x}
                                />
                            );
                        })}
                    </CalledBallsWrapper>
                </BallTray>
                <BallHoleLayer>
                    <BallHole>
                        {BALLS.map((ball) => {
                            return (
                                <BallHoleItem
                                    key={ball}
                                    y={currentBall === ball ? 0 : -1}
                                >
                                    <Ball size="big" number={ball} />
                                </BallHoleItem>
                            );
                        })}
                    </BallHole>
                    {showFireBallAnimation && (
                        <FireBallAnimation src={fireBall} alt="" />
                    )}
                </BallHoleLayer>
            </BallCallsContainer>
        );
    }
);
